



















  import Debug from 'debug'
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import { lazyInject } from '@/app/container';
  import { AspectService, AspectServiceSymbol } from '@/app/aspect/aspect-service';
  import { PresenterService, PresenterServiceSymbol } from '@/app/presenter/presenter-service';
  import {
    IncludeComponentsService,
    IncludeComponentsServiceSymbol
  } from '@/app/presenter/includer/include-components-service';
  import { EventStream, EventStreamSymbol } from '@/app/event-stream/event-stream';
  import { getFirstElement } from '@/app/shared/utils/content-utils';
  import { CourseAspect, TocItem } from '@/app/shared/models';
  import {
    SectionNavigationService,
    SectionNavigationServiceSymbol
  } from '@/app/statistics/section/section-navigation-service';
  import { ZoomableService, ZoomableServiceSymbol } from '@/app/zoomable/zoomable-service';
  import { setDocumentTitle } from '@/app/shared/utils/document-utils';
  import aspectStoreModule from '@/app/aspect/apsect-state';
  import BottomNavigationComponent from '@/app/shared/components/bottom-navigation-component.vue';
  import PageProgressComponent from '@/app/presenter/components/page-progress-component.vue';
  import PageComponent from '@/app/presenter/components/page-component.vue';
  import IntroComponent from '@/app/presenter/components/intro-component.vue';
  import CongratsComponent from '@/app/presenter/components/congrats-component.vue';
  import ErrorPageComponent from '@/app/presenter/components/error-page-component.vue';
  import BreadcrumbComponent from '@/app/breadcrumb/components/breadcrumb-component.vue';
  import DemoComponent from '@/app/presenter/components/demo-component.vue';
  import { ChildComponents } from "@/app/shared/utils/child-components";
  import { CancelToken, cancelToken, cancel, reset } from '@/app/shared/utils';
import { Cancelable } from 'lodash'

  const debug = Debug('presenter-component')

  @Component({
    components: {
      BottomNavigationComponent,
      PageProgressComponent,
      PageComponent,
      IntroComponent,
      CongratsComponent,
      ErrorPageComponent,
      BreadcrumbComponent,
      DemoComponent
    }
  })
  export default class PresenterComponent extends Vue {
    @Prop({
      default: null
    })
    public page: string|null;

    public content: string|null = null;

    public isIntro: boolean = false;

    public visibleBreadcrumb: boolean = false;

    public isMobile: boolean = false;

    @lazyInject(IncludeComponentsServiceSymbol)
    private includeComponentsService: IncludeComponentsService;

    @lazyInject(PresenterServiceSymbol)
    private presenterService: PresenterService;

    @lazyInject(AspectServiceSymbol)
    private aspectService: AspectService;

    @lazyInject(SectionNavigationServiceSymbol)
    private sectionNavigationService: SectionNavigationService;

    @lazyInject(ZoomableServiceSymbol)
    private zoomableService: ZoomableService;

    private childComponents: ChildComponents= new ChildComponents('presenter-component')

    private queryCancelToken: CancelToken = cancelToken()

    public get aspect(): CourseAspect|null {
      return aspectStoreModule.selectedCourseAspectInstance;
    }

    public get flatPages(): string[] {
      return aspectStoreModule.flatPages;
    }

    public showBreadcrumb(): void {
      const mainContent = this.$refs.mainContent as HTMLElement;

      if (this.content !== null && this.page && !this.isMobile && mainContent !== undefined) {
        mainContent.getBoundingClientRect().top < -20
          ? this.visibleBreadcrumb = true
          : this.visibleBreadcrumb = false;
      }
    }

    public checkWidth(): void {
      window.innerWidth < 1024
        ? this.isMobile = true
        : this.isMobile = false;
    }

    public get componentParam(): string|null {
      return this.$route.query && this.$route.query.c
        ? this.$route.query.c.toString()
        : null;
    }

    public get currentSection(): TocItem|null {
      if (this.page === null) {
        return null;
      }

      return this.aspectService.calculateSectionByPage(this.page);
    }

    public async mounted(): Promise<void> {
      reset(this.queryCancelToken)

      if (this.aspect !== null && this.page !== null) {
        await this.init(this.page, this.queryCancelToken);
      }

      if (this.currentSection !== null) {
        this.sectionNavigationService.openSection(this.currentSection.id, this.currentSection.title);
      }

      this.zoomableService.listen();

      this.setDocumentTitle();

      this.checkWidth();
    }

    public beforeDestroy(): void {
      this.childComponents.destroy()

      this.zoomableService.unlisten();
    }

    private destroyed() {
      cancel(this.queryCancelToken)
    }

    @Watch('$route.query.section')
    public onSectionQueryChanged(newValue: string): void {
      const section: TocItem|undefined = aspectStoreModule.sectionsById[newValue];
      if (!section) {
        return;
      }

      this.sectionNavigationService.openSection(section.id, section.title);
    }

    private async init(page: string, cancelToken: CancelToken): Promise<void> {
      debug('init')

      if (this.currentSection !== null) {
        await this.aspectService.calculateSelectedChapter(this.currentSection.id, true);
      }

      try {
        // get the content
        this.content = await this.getContent(page, cancelToken);
        
      } catch (error) {
        if (error.response.status === 404) {
          // wait for another cycle
          return;
        }

        throw error;
      }

      const parsed: any = $.parseHTML(this.content);
      const $parsedContent: any = $(parsed);

      // is intro
      this.isIntro = this.getIsIntro($parsedContent);

      // include components
      const components = await this.includeComponentsService.includeComponents($parsedContent, cancelToken);
      this.childComponents.register(components)
    }

    private async getContent(page: string, cancelToken: CancelToken): Promise<string> {
      let content = await this.presenterService.getContent(page, cancelToken);
      content = this.addLinksTargets(content);
      return this.wrapCoverDiv(content);
    }

    private addLinksTargets(content: string): string {
      const $content = $('<div/>', { html: content });
      const linkSelector: string = 'a[href^="http"]:not([target])';
      const linksCount = $(linkSelector, $content).length;
      if (linksCount === 0) {
        return content;
      }

      $content.find(linkSelector).attr('target', '_blank');

      return $content.html();
    }

    private wrapCoverDiv(content: string): string {
      const $content = $('<div/>', { html: content });
      const coverSelector: string = '.cover.component';
      const $cover = getFirstElement(coverSelector, $content);
      if ($cover === null) {
        return content;
      }

      const $titleElement = $cover.find('.cover__title *').first();
      $titleElement.addClass('section-title');
      const title: string = $titleElement[0].outerHTML;

      const wrapped: string = `<div class="cover-header" style="${$cover.attr('style')}">${title}</div>`;
      $content.find(coverSelector).replaceWith(wrapped);

      return $content.html();
    }

    private getIsIntro($parsedContent: any): boolean {
      return $parsedContent.filter('.intro.component').length > 0;
    }

    private setDocumentTitle(): void {
      let items: string[] = [];
      if (this.currentSection !== null) {
        items = [...items, this.currentSection.title];
      }

      if (aspectStoreModule.selectedChapter !== null) {
        items = [...items, aspectStoreModule.selectedChapter.title];
      }

      setDocumentTitle(items);
    }
  }
